import React from 'react';
import ReactDOM from 'react-dom/client';
import 'formiojs/dist/formio.full.min.css';
import './styles/formio-builder.css';
import App from './App.js';

const formioStyles = document.createElement('style');
formioStyles.textContent = `
  /* Staff (GHL-matched) design tokens */
  :root {
    --ci-font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --ci-primary: #2563eb;
    --ci-primary-hover: #1d4ed8;
    --ci-primary-light: #eff6ff;
    --ci-primary-focus: rgba(37,99,235,0.1);
    --ci-border: #cbd5e1;
    --ci-border-light: #e2e8f0;
    --ci-text: #111827;
    --ci-text-body: #374151;
    --ci-text-muted: #6b7280;
    --ci-surface: #f8fafc;
    --ci-radius: 6px;
    --ci-radius-lg: 10px;
  }

  /* Patient portal design tokens — scoped to .ci-portal */
  .ci-portal {
    --ci-font: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    --ci-primary: #17776b;
    --ci-primary-hover: #135f55;
    --ci-primary-light: #eaf5f3;
    --ci-primary-focus: rgba(23,119,107,0.12);
    --ci-border: #e5e2dd;
    --ci-border-light: #f0ede8;
    --ci-text: #1a2744;
    --ci-text-body: #3d3d52;
    --ci-text-muted: #6e7280;
    --ci-surface: #fafaf8;
    --ci-radius: 8px;
    --ci-radius-lg: 12px;
    --ci-success: #1f7a5c;
    --ci-success-light: #e6f4ee;
  }

  /* Success token also available to the staff :root for shared components */
  :root {
    --ci-success: #1f7a5c;
    --ci-success-light: #e6f4ee;
  }

  /* Patient chat motion (scoped, reduced-motion aware) */
  @keyframes ci-pulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 1; } }
  .ci-skeleton { animation: ci-pulse 1.4s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .ci-enter, .ci-enter-fast, .ci-fade, .ci-skeleton, .ci-slide-in { animation: none !important; }
  }

  /* Visible focus rings for the chat textarea + inline inputs (inline styles can't do :focus) */
  .ci-portal textarea:focus,
  .ci-portal .ci-focusable:focus { outline: none; border-color: var(--ci-primary); box-shadow: 0 0 0 3px var(--ci-primary-focus); }
  .ci-portal .ci-send-btn:hover:not(:disabled) { background: var(--ci-primary-hover); }
  .ci-portal .ci-mode-btn:hover:not([data-active="true"]) { border-color: var(--ci-primary); color: var(--ci-primary); }

  /* ── Patient-facing form (public form + assigned form) ─────────────────── */
  .ci-formio-form .form-group { margin-bottom: 18px; }
  .ci-formio-form .form-group label { font-size: 14px; font-weight: 500; color: var(--ci-text-body); margin-bottom: 6px; display: block; font-family: var(--ci-font); }
  .ci-formio-form .form-control { width: 100%; padding: 11px 14px; min-height: 44px; line-height: 1.4; border: 1px solid var(--ci-border); border-radius: var(--ci-radius); font-size: 15px; color: var(--ci-text); box-sizing: border-box; transition: border-color 0.15s; font-family: var(--ci-font); }
  .ci-formio-form .form-control:focus,
  .ci-formio-form .form-control:focus-visible { outline: none; border-color: var(--ci-primary); box-shadow: 0 0 0 3px var(--ci-primary-focus); }
  .ci-formio-form .form-control::placeholder { color: var(--ci-text-muted); }
  .ci-formio-form textarea.form-control { min-height: 88px; }
  .ci-formio-form select.form-control { min-height: 44px; padding-right: 36px; }
  .ci-formio-form .btn-wizard-nav-submit,
  .ci-formio-form .btn-primary { background: var(--ci-primary); color: #fff; border: none; border-radius: var(--ci-radius); padding: 12px 24px; min-height: 44px; cursor: pointer; font-size: 14px; font-weight: 600; margin-top: 8px; font-family: var(--ci-font); transition: background 0.15s, transform 0.1s; }
  .ci-formio-form .btn-primary:hover { background: var(--ci-primary-hover); transform: translateY(-1px); }
  .ci-formio-form .radio-inline { display: inline-flex; align-items: center; gap: 6px; margin-right: 16px; min-height: 44px; cursor: pointer; font-size: 14px; color: var(--ci-text-body); font-family: var(--ci-font); }
  .ci-formio-form .checkbox label { display: inline-flex; align-items: center; min-height: 44px; cursor: pointer; font-size: 14px; color: var(--ci-text-body); font-family: var(--ci-font); }
  .ci-formio-form input[type=radio], .ci-formio-form input[type=checkbox] { width: 20px; height: 20px; accent-color: var(--ci-primary); }
  .ci-formio-form fieldset { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius-lg); padding: 16px 18px; margin-bottom: 16px; }
  .ci-formio-form legend { font-size: 14px; font-weight: 600; color: var(--ci-primary); padding: 0 8px; font-family: var(--ci-font); }
  .ci-formio-form .panel { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius-lg); margin-bottom: 20px; overflow: hidden; }
  .ci-formio-form .panel-heading { background: var(--ci-surface); padding: 12px 16px; border-bottom: 1px solid var(--ci-border-light); }
  .ci-formio-form .panel-title { font-size: 14px; font-weight: 600; color: var(--ci-text-body); font-family: var(--ci-font); }
  .ci-formio-form .panel-body { padding: 16px; }
  .ci-formio-form .row { display: flex; flex-wrap: wrap; gap: 12px; }
  .ci-formio-form .col-sm-6, .ci-formio-form .col-md-6 { flex: 1; min-width: 220px; }
  .ci-formio-form .formio-errors .error { color: #b91c1c; font-size: 12px; margin-top: 4px; font-family: var(--ci-font); }
  /* Form.io fires a hardcoded green "Submission Complete" success alert on submit.
     The app shows its OWN success screen, so hide only that banner (submitMessage is
     a no-op in formiojs 4.x). Keep the danger/validation alert visible + on-brand so
     patients can see which required field is blocking submission. */
  .ci-formio-form .alert-success { display: none; }
  .ci-formio-form .alert-danger { color: #b91c1c; background: #fef2f2; border: 1px solid #fca5a5; border-radius: var(--ci-radius); padding: 10px 14px; font-size: 13px; margin-bottom: 16px; font-family: var(--ci-font); }
  .ci-formio-form select.form-control { appearance: auto; }
  /* Form.io native signature widget */
  .ci-formio-form .formio-component-signature .signature-pad-body { border: 1px solid var(--ci-border); border-radius: var(--ci-radius); background: var(--ci-surface); }
  .ci-formio-form .formio-component-signature .btn { background: #fff; border: 1px solid var(--ci-border); color: var(--ci-text-body); border-radius: var(--ci-radius); min-height: 40px; }
  @media (max-width: 767px) {
    .ci-formio-form .col-sm-6, .ci-formio-form .col-md-6 { flex: 1 1 100%; min-width: 0; }
    .ci-formio-form .form-control { font-size: 16px; }
  }

  /* ── Staff preview slide-over (compact) ────────────────────────────────── */
  .ci-formio-preview .form-group label { font-size: 13px; font-weight: 500; color: var(--ci-text-body); margin-bottom: 4px; display: block; font-family: var(--ci-font); }
  .ci-formio-preview .form-control { width: 100%; padding: 7px 10px; border: 1px solid var(--ci-border); border-radius: var(--ci-radius); font-size: 13px; color: var(--ci-text); box-sizing: border-box; font-family: var(--ci-font); }
  .ci-formio-preview .btn-primary { background: var(--ci-primary); color: #fff; border: none; border-radius: var(--ci-radius); padding: 7px 16px; cursor: pointer; font-size: 13px; font-weight: 500; font-family: var(--ci-font); }
  .ci-formio-preview .radio-inline { display: inline-flex; align-items: center; gap: 5px; margin-right: 12px; font-size: 13px; font-family: var(--ci-font); }
  .ci-formio-preview .checkbox { font-size: 13px; font-family: var(--ci-font); }
  .ci-formio-preview fieldset { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius); padding: 12px 14px; margin-bottom: 12px; }
  .ci-formio-preview legend { font-size: 13px; font-weight: 600; color: var(--ci-primary); padding: 0 6px; font-family: var(--ci-font); }
  .ci-formio-preview .panel { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius-lg); margin-bottom: 16px; overflow: hidden; }
  .ci-formio-preview .panel-heading { background: var(--ci-surface); padding: 10px 14px; border-bottom: 1px solid var(--ci-border-light); }
  .ci-formio-preview .panel-title { font-size: 13px; font-weight: 600; color: var(--ci-text-body); font-family: var(--ci-font); }
  .ci-formio-preview .panel-body { padding: 14px; }
  .ci-formio-preview .row { display: flex; flex-wrap: wrap; gap: 8px; }
  .ci-formio-preview .col-sm-6, .ci-formio-preview .col-md-6 { flex: 1; min-width: 200px; }

  /* ── Staff submission review (read-only) ───────────────────────────────── */
  .ci-formio-review .form-group { margin-bottom: 14px; }
  .ci-formio-review .form-group label { font-size: 13px; font-weight: 600; color: var(--ci-text-body); margin-bottom: 3px; display: block; font-family: var(--ci-font); }
  .ci-formio-review .form-control { background: var(--ci-surface); color: var(--ci-text); border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius); padding: 6px 10px; font-size: 13px; width: 100%; box-sizing: border-box; font-family: var(--ci-font); }
  .ci-formio-review .form-control[disabled],
  .ci-formio-review .form-control[readonly] { background: var(--ci-surface); color: var(--ci-text); }
  /* Hide all submit / action buttons in read-only view */
  .ci-formio-review .formio-component-submit,
  .ci-formio-review .btn-primary,
  .ci-formio-review .btn-wizard-nav-submit,
  .ci-formio-review button[type="submit"] { display: none !important; }
  /* Hide the inline Form.io file component in the review — its download link is
     session-less and confusing; staff download via the dedicated Attachments panel. */
  .ci-formio-review .formio-component-file { display: none !important; }
  .ci-formio-review fieldset { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius); padding: 12px 14px; margin-bottom: 12px; }
  .ci-formio-review legend { font-size: 13px; font-weight: 600; color: var(--ci-primary); padding: 0 6px; font-family: var(--ci-font); }
  .ci-formio-review .panel { border: 1px solid var(--ci-border-light); border-radius: var(--ci-radius-lg); margin-bottom: 16px; overflow: hidden; }
  .ci-formio-review .panel-heading { background: var(--ci-surface); padding: 10px 14px; border-bottom: 1px solid var(--ci-border-light); }
  .ci-formio-review .panel-title { font-size: 13px; font-weight: 600; color: var(--ci-text-body); font-family: var(--ci-font); }
  .ci-formio-review .panel-body { padding: 14px; }
  .ci-formio-review .row { display: flex; flex-wrap: wrap; gap: 8px; }
  .ci-formio-review .col-sm-6, .ci-formio-review .col-md-6 { flex: 1; min-width: 180px; }
  .ci-formio-review .radio-inline { display: inline-flex; align-items: center; gap: 5px; margin-right: 10px; font-size: 13px; font-family: var(--ci-font); }
  .ci-formio-review .checkbox label { font-size: 13px; font-family: var(--ci-font); }
`;
document.head.appendChild(formioStyles);

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
